import clsx from 'clsx';
import React, { useRef, useState, useEffect, useId } from 'react';
import { useFloatingElement } from '../../utils';
import { useModalContext } from '../Modal';
import { Portal } from '../Portal';
import './Tooltip.css';
/**
 * `<Tooltip>` only accepts one child element as its trigger and it should be able to accept a Ref to attach the tooltip content onto. It can also be a `string` which will be wrapped in a `span` element.
 */
export function Tooltip(props) {
    const { children, className, trigger, placement = 'top', delay = 0, delayIn = delay, delayOut = delay, tabIndex = 0, offset, offsetX, offsetY, UNSAFE_allowHover, } = props;
    const { initialized: modalInitialized } = useModalContext();
    const [showTooltip, setShowTooltip] = useState(false);
    const timeoutRef = useRef();
    const id = useId();
    const { anchor, floating, floatingStyles, refs } = useFloatingElement({
        placement,
        preferFlip: true,
    });
    const createShowTooltipHandler = (show) => () => {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => {
            setShowTooltip(show);
        }, show
            ? delayIn
            : UNSAFE_allowHover
                ? delayOut < 350
                    ? 350
                    : delayOut
                : delayOut);
    };
    const handleKeyUp = (e) => {
        if (e.key !== 'Escape')
            return;
        setShowTooltip(false);
    };
    // clean up when unmounting
    useEffect(() => () => {
        window.clearTimeout(timeoutRef.current);
    }, []);
    const triggerElement = typeof trigger === 'string' ? (React.createElement("span", null, trigger)) : trigger ? (React.Children.only(trigger)) : null;
    return (React.createElement(React.Fragment, null,
        triggerElement &&
            React.cloneElement(triggerElement, {
                'aria-describedby': showTooltip ? id : undefined,
                ref: anchor,
                tabIndex,
                onPointerEnter: createShowTooltipHandler(true),
                onPointerLeave: (e) => {
                    var _a, _b;
                    const hideTooltip = createShowTooltipHandler(false);
                    if (UNSAFE_allowHover) {
                        if ((e.relatedTarget instanceof HTMLElement ||
                            e.relatedTarget instanceof SVGElement) &&
                            !((_a = refs.floating.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget)) &&
                            !((_b = refs.anchor.current) === null || _b === void 0 ? void 0 : _b.contains(e.relatedTarget))) {
                            hideTooltip();
                        }
                    }
                    else {
                        hideTooltip();
                    }
                },
                onFocus: createShowTooltipHandler(true),
                onBlur: createShowTooltipHandler(false),
                onKeyUp: handleKeyUp,
                'data-no-autofocus': true,
            }),
        React.createElement(Portal, null, showTooltip && (React.createElement("div", { role: "tooltip", className: clsx('mollie-ui-tooltip', className), id: id, style: {
                ...floatingStyles,
                '--mollie-ui-floating-offset': offset,
                '--mollie-ui-floating-offset-x': offsetX,
                '--mollie-ui-floating-offset-y': offsetY,
                '--mollie-ui-floating-z-index': modalInitialized
                    ? 600
                    : undefined,
            }, ref: floating, onPointerEnter: () => {
                createShowTooltipHandler(true)();
                window.clearTimeout(timeoutRef.current);
            }, onPointerLeave: createShowTooltipHandler(false) },
            React.createElement("div", { className: "mollie-ui-tooltip-inner", "data-mollie-ui-theme": "dark" }, children))))));
}
//# sourceMappingURL=Tooltip.js.map